import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ServiceHistoryCard = ({ service, onViewDetails, onDownloadInvoice, onCreateSAV }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getStatusColor = (status) => {
    const colors = {
      completed: 'bg-success/10 text-success',
      scheduled: 'bg-warning/10 text-warning',
      cancelled: 'bg-error/10 text-error',
      in_progress: 'bg-primary/10 text-primary'
    };
    return colors?.[status] || 'bg-muted text-muted-foreground';
  };

  const getStatusLabel = (status) => {
    const labels = {
      completed: 'Terminé',
      scheduled: 'Planifié',
      cancelled: 'Annulé',
      in_progress: 'En cours'
    };
    return labels?.[status] || status;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateWarrantyDaysLeft = (completionDate) => {
    const completion = new Date(completionDate);
    const warrantyEnd = new Date(completion);
    warrantyEnd?.setMonth(warrantyEnd?.getMonth() + 6);
    const today = new Date();
    const daysLeft = Math.ceil((warrantyEnd - today) / (1000 * 60 * 60 * 24));
    return daysLeft > 0 ? daysLeft : 0;
  };

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden hover:shadow-md transition-shadow">
      <div className="p-4 lg:p-6">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-4">
          <div className="flex-1">
            <div className="flex items-start gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name={service?.icon} size={20} color="var(--color-primary)" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base lg:text-lg font-semibold text-foreground mb-1">
                  {service?.title}
                </h3>
                <p className="text-sm text-muted-foreground">
                  Référence: {service?.reference}
                </p>
              </div>
            </div>
            
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(service?.status)}`}>
                <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
                {getStatusLabel(service?.status)}
              </span>
              
              {service?.warranty && service?.status === 'completed' && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-success/10 text-success">
                  <Icon name="Shield" size={12} />
                  Garantie: {calculateWarrantyDaysLeft(service?.completionDate)} jours restants
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-col items-start lg:items-end gap-2">
            <div className="text-right">
              <p className="text-2xl font-bold text-foreground">{service?.price}</p>
              <p className="text-xs text-muted-foreground">TVA incluse</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
              iconPosition="right"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? 'Réduire' : 'Détails'}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Icon name="Calendar" size={16} />
            <span>{formatDate(service?.date)}</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Icon name="MapPin" size={16} />
            <span className="truncate">{service?.address}</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Icon name="User" size={16} />
            <span>{service?.technician}</span>
          </div>
        </div>

        {isExpanded && (
          <div className="mt-6 pt-6 border-t border-border animate-slide-up">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-3">Détails de l'intervention</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Équipement:</span>
                    <span className="font-medium text-foreground">{service?.equipment}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Problème:</span>
                    <span className="font-medium text-foreground">{service?.problem}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Durée:</span>
                    <span className="font-medium text-foreground">{service?.duration}</span>
                  </div>
                  {service?.notes && (
                    <div className="mt-3 p-3 bg-muted rounded-lg">
                      <p className="text-xs text-muted-foreground mb-1">Notes du technicien:</p>
                      <p className="text-sm text-foreground">{service?.notes}</p>
                    </div>
                  )}
                </div>
              </div>

              {service?.photos && service?.photos?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-3">Photos de l'intervention</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {service?.photos?.map((photo, index) => (
                      <div key={index} className="relative aspect-video rounded-lg overflow-hidden bg-muted">
                        <Image
                          src={photo?.url}
                          alt={photo?.alt}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                          <p className="text-xs text-white">{photo?.label}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-2 mt-6">
              <Button
                variant="outline"
                size="sm"
                iconName="Download"
                iconPosition="left"
                onClick={() => onDownloadInvoice(service?.id)}
              >
                Télécharger facture
              </Button>
              
              {service?.warranty && service?.status === 'completed' && (
                <Button
                  variant="outline"
                  size="sm"
                  iconName="AlertCircle"
                  iconPosition="left"
                  onClick={() => onCreateSAV(service?.id)}
                >
                  Créer un ticket SAV
                </Button>
              )}
              
              <Button
                variant="ghost"
                size="sm"
                iconName="ExternalLink"
                iconPosition="left"
                onClick={() => onViewDetails(service?.id)}
              >
                Voir détails complets
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceHistoryCard;